import { createContext, useEffect, useState } from 'react';
import axios from 'axios';
import { cloneDeep } from 'lodash';
import * as AWS from 'aws-sdk';
import * as cryptoJS from 'crypto-js';
import { Buffer } from 'buffer';

const OverviewContext = createContext({
	overviewData: [],
	linkIsValid: false,
	isLoading: false,
	tabValue: '',
	paymentMethod: null,
	createObj: {},
	toasterMsg: false,
	paymentPlanObj: {},
	callbackLaterObj: {},
	searchURL: '',
	setIsLoading: e => {},
	setOverview: e => {},
	setLinkValidity: e => {},
	setTabValue: e => {},
	setpaymentMethod: e => {},
	setCreateObj: e => {},
	resetCreateObj: e => {},
	api: e => {},
	reset: e => {},
	setToaster: e => {},
	setToasterMsg: e => {},
	setPaymentPlanObj: e => {},
	setCallbackLaterObj: e => {},
	setSearchURL: e => {}
});

export const OverviewContextProvider = props => {
	const [searchURL, setSearchURL] = useState('');
	const [overviewData, setOverviewData] = useState(null);
	const [isLoading, setIsLoading] = useState(null);
	const [linkIsValid, setLinkValidity] = useState(false);
	const [createObj, setCreateObj] = useState({});
	const [paymentMethod, setpaymentMethod] = useState(null);
	const [tabValue, setTabValue] = useState('home');
	const [toasterMsg, setToasterMsg] = useState(null);
	const [paymentPlanObj, setPaymentPlanObj] = useState({});
	const [callbackLaterObj, setCallbackLaterObj] = useState({});
	const [langObj, setLangObj] = useState();
	const [preferredLang, setPreferredLang] = useState([{ key: 'ENGLISH', value: 'English' }]);
	const [selectedLang, setSelectedLang] = useState('');

	const setToaster = e => {
		setToasterMsg({
			information: e?.information,
			error: e?.error,
			success: e?.success,
			warning: e?.warning,
			center: e?.center,
			header: e?.header,
			content: e?.content,
			undo: e?.undo,
			buttontext: e?.buttontext,
			hideclose: e?.hideclose,
			handleUndoClose: e?.handleUndoClose,
			top: e?.top
		});
		setTimeout(() => {
			setToasterMsg(null);
		}, 4000);
	};

	const request = async options => {
		try {
			const client = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
			client.defaults.headers.common['c_id'] = localStorage.getItem('c_id') || null;
			const response = await client(options);
			return Promise.resolve(response);
		} catch (error) {
			return Promise.reject(error.response);
		}
	};

	// common api, obj should be object with url, method and data keys
	const api = async obj => {
		try {
			setIsLoading(true);
			obj.method !== 'get' && obj?.data && !(obj.data instanceof FormData) && process.env.REACT_APP_ENABLE_AWS_ENCRYPTION === 'Y' && (await encryptRequest(obj));
			const res = await request(obj);
			setIsLoading(false);
			obj.reset && reset();
			obj.successToaster && setToaster({ header: 'Success', content: res?.data?.data?.message, success: true });
			if (res?.data?.data && Object.keys(res.data.data)?.find(elm => elm === 'encryptedKey') && process.env.REACT_APP_ENABLE_AWS_ENCRYPTION === 'Y') {
				res.data.data = await decryptResponse(res.data.data);
			}
			return Promise.resolve(res);
		} catch (error) {
			const err = error?.data?.message || error?.message || 'Something went wrong!';
			setIsLoading(false);
			setToaster({ error: true, header: 'Error!', content: err });
			return Promise.reject(error);
		}
	};

	const kms = new AWS.KMS({
		accessKeyId: process.env.REACT_APP_ENCRYPTION_ACCESSKEY_ID,
		secretAccessKey: process.env.REACT_APP_ENCRYPTION_SECRET_ACCESSKEY,
		region: process.env.REACT_APP_ENCRYPTION_REGION
	});

	const encryptRequest = async obj => {
		try {
			const key = await kms.encrypt({ KeyId: process.env.REACT_APP_ENCRYPTION_KMS_KEYID, Plaintext: process.env.REACT_APP_ENCRYPTION_SECRET_KEY }).promise();
			obj.data = {
				encryptedKey: key.CiphertextBlob.toString('base64'),
				encryptedBody: cryptoJS.AES.encrypt(JSON.stringify(obj.data), process.env.REACT_APP_ENCRYPTION_SECRET_KEY).toString()
			};
			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const decryptResponse = async res => {
		try {
			const decryptParams = { CiphertextBlob: Buffer.from(res?.encryptedKey, 'base64'), KeyId: process.env.REACT_APP_ENCRYPTION_KMS_KEYID };
			const decryptedKey = await kms.decrypt(decryptParams).promise();
			const bytes = cryptoJS.AES.decrypt(res?.encryptedData, decryptedKey.Plaintext.toString('utf-8'));
			const decryptedData = bytes.toString(cryptoJS.enc.Utf8);
			return Promise.resolve(JSON.parse(decryptedData));
		} catch (error) {
			return Promise.reject(error);
		}
	};

	const setOverview = data => {
		setOverviewData(data);
	};

	const resetCreateObj = () => {
		const { ci_id, c_id, bd_batchID, bd_token, ci_accountNo, p_code, customerInfo, bd_dueAmt } = overviewData;
		const data = { ci_id, c_id, bd_batchID, bd_token, ci_accountNo, p_code, customerInfo, accountName: customerInfo?.at()?.ci_customerNameUnformatted };
		overviewData &&
			setCreateObj(prevState => {
				const state = cloneDeep(prevState);
				data['ts_amount'] = state.ts_amount || bd_dueAmt;
				return data;
			});
	};

	const reset = () => {
		resetCreateObj();
		setpaymentMethod(null);
		setTabValue('home');
		setPaymentPlanObj(overviewData?.paymentPlan?.at()?.ah_details || {});
		setCallbackLaterObj(overviewData?.callbackLater?.at()?.ah_details || {});
	};

	useEffect(() => {
		overviewData && reset();
	}, [overviewData]);

	return (
		<OverviewContext.Provider
			value={{
				overviewData,
				linkIsValid,
				isLoading,
				tabValue,
				paymentMethod,
				createObj,
				toasterMsg,
				paymentPlanObj,
				callbackLaterObj,
				searchURL,
				langObj,
				preferredLang,
				selectedLang,
				setTabValue,
				setIsLoading,
				setOverview,
				setLinkValidity,
				setpaymentMethod,
				setCreateObj,
				resetCreateObj,
				setToaster,
				setToasterMsg,
				api,
				reset,
				setPaymentPlanObj,
				setCallbackLaterObj,
				setSearchURL,
				setLangObj,
				setPreferredLang,
				setSelectedLang
			}}
		>
			{props.children}
		</OverviewContext.Provider>
	);
};

export default OverviewContext;
